import React, { useState, useEffect, useRef } from 'react'; // Import useRef
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Send,
  X,
  ShieldCheck,
  Lock,
  Database,
  PhoneCall, // Nuevo icono para el control de telefonía
  PhoneOff, // Icono para la falta de control
  FileWarning,
  Link,
  TrendingUp,
  Briefcase, // Icono para líneas locales fijas
  Smartphone, // Icono para líneas móviles
  Globe, // Icono para 0800
  DollarSign, // Icono para el ahorro
  UserCheck, // Icono para identificador inteligente (cliente reconocido)
  Settings, // Icono para anexos
  Signal, // Icono para líneas IP
  Cloud, // Nuevo icono para centralita virtual
  Zap, // Nuevo icono para Voicebot
} from 'lucide-react';
import ParticlesBackground from '../components/ParticlesBackground';
import LanguageSwitcher from '../components/LanguageSwitcher';

import ContactForm from '../ContactForm';

// --- COMPONENTES DE PÁGINA REUTILIZADOS ---

const PageHeader = ({ onOpenPopup, t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </a>
      <div className="flex items-center gap-4">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {t('common.free_diagnostic')}
        </button>
      </div>
    </div>
  </header>
);


// Componente reutilizable para tarjetas de características
const FeatureCard = ({ icon: Icon, title, text, colorClass, bgColorClass = "bg-gray-50", borderColorClass = "border-gray-200" }) => (
  <div className={`${bgColorClass} ${borderColorClass} border rounded-lg p-5 h-full transition hover:shadow-md`}>
    <div className="flex items-center gap-3 mb-2">
      <Icon className={colorClass} size={24} />
      <h4 className="font-semibold text-[#141414]">{title}</h4>
    </div>
    <p className="text-sm text-gray-700">{text}</p>
  </div>
);

// --- COMPONENTE ESPECÍFICO DE TELEFONÍA IP ---

const TelefoniaPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Estado para popup de contacto (fallback)
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const openPopup = () => { setIsPopupOpen(true); document.body.style.overflow = 'hidden'; };
  const closePopup = () => { setIsPopupOpen(false); document.body.style.overflow = 'auto'; };

  // Refs para controlar la inicialización única
  const scriptsLoadedRef = useRef(false); // Ref para controlar la carga de TODOS los scripts
  const widgetInitializedRef = useRef(false);
  const domElementsCreatedRef = useRef(false); // Ref para controlar creación de divs

  // Función para manejar la llamada con fallback a popup
  const handleCall = () => {
    if (typeof window === 'undefined') return;
    openPopup();
  };

  // Carga e inicialización de scripts locales de Zadarma
  useEffect(() => {

    if (typeof window === 'undefined') return;

    const scriptIds = {
        webrtc: 'zadarmaWebRTC',
        jssip: 'zadarmaJssip',
        widget: 'zadarmaWidget'
    };
    const cssId = 'zadarmaStyle';
    const widgetContainerId = 'myZadarmaCallmeWidget16188';
    const rootContainerId = 'zadarmaRoot'; // Contenedor raíz invisible

    // Función para inicializar el widget (definida aquí para closure)
    const initializeZadarmaWidget = () => {
        // Solo inicializar si no se ha hecho antes y la clase existe
        if (!widgetInitializedRef.current && typeof window.ZadarmaCallmeWidget !== 'undefined') {
            console.log('Attempting to initialize Zadarma widget...');

            // Asegurar que el contenedor específico del widget exista
            let widgetContainer = document.getElementById(widgetContainerId);
             if (!widgetContainer) {
                 console.error(`Zadarma widget container #${widgetContainerId} not found during init.`);
                 // Intentar crear el contenedor si no existe
                 widgetContainer = document.createElement('div');
                 widgetContainer.id = widgetContainerId;
                 const root = document.getElementById(rootContainerId);
                 if (root) {
                     root.appendChild(widgetContainer);
                 } else {
                     console.error('Zadarma root container not found or creatable during init.');
                     return; // No continuar si el root no está
                 }
             }

            console.log('Creating Zadarma widget instance...');
            try {
                // eslint-disable-next-line
                window.myZadarmaCallmeWidget16188 = new window.ZadarmaCallmeWidget(widgetContainerId);
                window.myZadarmaCallmeWidget16188.create({
                    "widgetId": "FyntfZrnub3CYK7CAKvG1h8Zp1PP5kgcaSf6UT963EbCgX3gcx345dtfxpcd2v9xmA7bYrSbmdxjL2S63kgm9Yvjfsf852zu631128e3a15d44120f4cb288cbcb79b0",
                    "sipId":"493239",
                    "domElement": widgetContainerId
                }, {
                    "shape":"square", "language":"es", "width":"220", "dtmf":false, "font": "'Trebuchet MS','Helvetica CY',sans-serif", "color_call": "rgb(255, 255, 255)", "color_bg_call": "rgb(3,3,3)", "color_border_call": "rgb(255,255,255)", "is_custom_hover": 0, "color_call_hover": "rgb(255, 255, 255)", "bg_call_hover": "rgb(100, 167, 26)", "border_call_hover": "rgb(178, 211, 141)", "color_connection": "rgb(255, 255, 255)", "color_bg_connection": "rgb(96,219,92)", "color_border_connection": "rgb(255,255,255)", "color_calling": "rgb(255, 255, 255)", "color_border_calling": "rgb(255,127,127)", "color_bg_calling": "rgb(219,51,51)", "color_ended": "rgb(255, 255, 255)", "color_bg_ended": "rgb(164,164,164)", "color_border_ended": "rgb(210, 210, 210)"
                });
                 console.log('Zadarma widget created successfully.');
                 widgetInitializedRef.current = true; // Marcar como inicializado
            } catch (error) {
                 console.error('Error creating Zadarma widget:', error);
                 widgetInitializedRef.current = false; // Marcar como fallido para posible reintento
            }
        } else if (widgetInitializedRef.current) {
             console.log('Zadarma widget instance already initialized.');
        } else {
             console.warn('ZadarmaCallmeWidget class not defined. Cannot initialize yet. Will retry shortly...');
             // Reintentar si la clase no está definida después de cargar el script
             setTimeout(initializeZadarmaWidget, 500); // Aumentar un poco el reintento
        }
    };

     // Hacer la función de inicialización accesible globalmente (requerido por algunos widgets)
     window.initializeZadarmaWidget = initializeZadarmaWidget;


    // Crear contenedores DOM necesarios UNA SOLA VEZ
    if (!domElementsCreatedRef.current) {
        // Contenedor raíz
         if (!document.getElementById(rootContainerId)) {
            const rootDiv = document.createElement('div');
            rootDiv.id = rootContainerId;
            rootDiv.style.position = 'fixed';
            rootDiv.style.bottom = '0';
            rootDiv.style.right = '0';
            rootDiv.style.zIndex = '1000';
            document.body.appendChild(rootDiv);
         }
         // Contenedor específico del widget (se crea aquí para asegurar existencia)
         if (!document.getElementById(widgetContainerId)) {
             const widgetDiv = document.createElement('div');
             widgetDiv.id = widgetContainerId;
             const root = document.getElementById(rootContainerId);
             if (root) {
                 root.appendChild(widgetDiv);
             } else {
                  console.error('Zadarma root container could not be found or created.');
             }
         }
         domElementsCreatedRef.current = true; // Marcar como creados
    }

    // Cargar CSS con fallback: local -> CDN
    if (!document.getElementById(cssId)) {
        const link = document.createElement('link');
        link.id = cssId;
        link.rel = 'stylesheet';
        link.href = '/zadarma-widget/style.min.css';
        link.onerror = () => {
            console.warn('Local Zadarma CSS not found, falling back to CDN.');
            const cdn = document.createElement('link');
            cdn.id = cssId + '-cdn';
            cdn.rel = 'stylesheet';
            cdn.href = 'https://my.zadarma.com/webphoneWebRTCWidget/v8/css/style.min.css';
            document.head.appendChild(cdn);
        };
        document.head.appendChild(link);
    }

    // Cargar Scripts Secuencialmente SOLO SI no se han cargado antes
    if (!scriptsLoadedRef.current) { // Usar scriptsLoadedRef (plural ahora)

        const loadScriptWithFallback = (id, srcs, onLoadCallback) => {
            // Evitar doble inserción
            if (document.getElementById(id)) {
                console.log(`Script ${id} already in DOM.`);
                if (onLoadCallback) setTimeout(onLoadCallback, 0);
                return;
            }

            let index = 0;
            const tryLoad = () => {
                const src = srcs[index];
                if (!src) {
                    console.error(`All sources failed for script ${id}`);
                    return;
                }
                console.log(`Loading script ${id} from ${src}...`);
                const script = document.createElement('script');
                script.id = id;
                script.src = src;
                script.async = false;
                script.onload = () => {
                    console.log(`Script ${id} loaded successfully from ${src}.`);
                    if (onLoadCallback) onLoadCallback();
                };
                script.onerror = () => {
                    console.warn(`Failed to load script ${id} from ${src}. Trying next source...`);
                    // Eliminar nodo fallido y probar el siguiente
                    script.remove();
                    index += 1;
                    tryLoad();
                };
                document.body.appendChild(script);
            };

            tryLoad();
        };

        // Cargar secuencialmente con fallback local->CDN: WebRTC -> JSSIP -> Widget -> Inicializar
        loadScriptWithFallback(scriptIds.webrtc, ['/zadarma-widget/detectWebRTC.min.js', 'https://my.zadarma.com/webphoneWebRTCWidget/v8/js/detectWebRTC.min.js'], () => {
            loadScriptWithFallback(scriptIds.jssip, ['/zadarma-widget/jssip.min.js', 'https://my.zadarma.com/webphoneWebRTCWidget/v8/js/jssip.min.js'], () => {
                loadScriptWithFallback(scriptIds.widget, ['/zadarma-widget/widget.min.js', 'https://my.zadarma.com/webphoneWebRTCWidget/v8/js/widget.min.js'], () => {
                     scriptsLoadedRef.current = true; // Marcar TODOS los scripts como cargados
                     console.log("All Zadarma scripts loaded. Attempting initialization...");
                     initializeZadarmaWidget(); // Inicializar después de que el último script cargue
                });
            });
        });

    } else if (!widgetInitializedRef.current) {
        // Si los scripts ya se cargaron pero el widget no (ej: HMR), intentar inicializar
         console.log('Scripts already marked as loaded, attempting widget init again.');
         setTimeout(initializeZadarmaWidget, 100);
    }


    return () => {
        // Limpieza básica (opcional)
         console.log('TelefoniaPage cleanup potentially runs.');
         // No eliminar los scripts aquí para evitar problemas con HMR
         // delete window.initializeZadarmaWidget; // Podría romper re-renderizados
         // delete window.myZadarmaCallmeWidget16188;
    };
  }, []); // El array vacío asegura que esto se ejecute solo al montar/desmontar


  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <ParticlesBackground />

      {/* Popup Modal de contacto (fallback cuando la llamada no está disponible) */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('telefonia.popup_close')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('common.diagnostic_request')}</h3>
              <p className="text-gray-600 mb-6">{t('common.diagnostic_desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}

      <PageHeader onOpenPopup={handleCall} t={t} />

      {/* Fondo de partículas con mayor contraste en fondo claro */}
      {/* Mantener detrás del contenido y del header/popup */}
      

      {/* Contenido Principal */}
      <main className="relative z-10 pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">

          {/* Botón Volver */}
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          {/* Hero de la Página */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('telefonia.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              {t('telefonia.hero.subtitle')}
            </p>
          </div>

          {/* Sección Comparativa: Antes (Problema) y Después (Solución) */}
          <div className="grid md:grid-cols-2 gap-12">

            {/* Columna: ANTES (El Caos de las Líneas Individuales) */}
            <div>
              <div className="flex items-center justify-center bg-red-100 text-red-700 w-16 h-16 rounded-full mx-auto mb-6 shadow-md">
                <PhoneOff size={32} />
              </div>
              <h2 className="text-2xl font-bold text-center text-[#141414] mb-6">
                {t('telefonia.comparison.chaos_title')}
              </h2>
              <p className="text-center text-gray-600 mb-8">
                {t('telefonia.comparison.chaos_subtitle')}
              </p>

              <div className="space-y-6">
                <FeatureCard
                  icon={Lock}
                  colorClass="text-red-600"
                  title={t('telefonia.comparison.chaos_features.risk.title')}
                  text={t('telefonia.comparison.chaos_features.risk.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-red-200"
                />
                <FeatureCard
                  icon={FileWarning}
                  colorClass="text-red-600"
                  title={t('telefonia.comparison.chaos_features.supervision.title')}
                  text={t('telefonia.comparison.chaos_features.supervision.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-red-200"
                />
                <FeatureCard
                  icon={Database}
                  colorClass="text-red-600"
                  title={t('telefonia.comparison.chaos_features.crm.title')}
                  text={t('telefonia.comparison.chaos_features.crm.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-red-200"
                />
                <FeatureCard
                  icon={DollarSign}
                  colorClass="text-red-600"
                  title={t('telefonia.comparison.chaos_features.costs.title')}
                  text={t('telefonia.comparison.chaos_features.costs.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-red-200"
                />
              </div>
            </div>

            {/* Columna: DESPUÉS (La Solución con Telefonía IP Centralizada) */}
            <div>
              <div className="flex items-center justify-center bg-blue-100 text-blue-700 w-16 h-16 rounded-full mx-auto mb-6 shadow-md">
                <PhoneCall size={32} />
              </div>
              <h2 className="text-2xl font-bold text-center text-[#141414] mb-6">
                {t('telefonia.comparison.control_title')}
              </h2>
              <p className="text-center text-gray-600 mb-8">
                {t('telefonia.comparison.control_subtitle')}
              </p>

              <div className="space-y-6">
                <FeatureCard
                  icon={ShieldCheck}
                  colorClass="text-blue-600"
                  title={t('telefonia.comparison.control_features.traceability.title')}
                  text={t('telefonia.comparison.control_features.traceability.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-blue-200"
                />
                <FeatureCard
                  icon={Link}
                  colorClass="text-blue-600"
                  title={t('telefonia.comparison.control_features.integration.title')}
                  text={t('telefonia.comparison.control_features.integration.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-blue-200"
                />
                <FeatureCard
                  icon={UserCheck}
                  colorClass="text-blue-600"
                  title={t('telefonia.comparison.control_features.ivr.title')}
                  text={t('telefonia.comparison.control_features.ivr.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-blue-200"
                />
                <FeatureCard
                  icon={Settings}
                  colorClass="text-blue-600"
                  title={t('telefonia.comparison.control_features.savings.title')}
                  text={t('telefonia.comparison.control_features.savings.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-blue-200"
                />
              </div>
            </div>
          </div>

          {/* SECCIÓN NUEVA: CENTRALITA VIRTUAL Y VOICEBOTS (Nueva sección clave) */}
          <section className="mt-20 p-8 bg-white rounded-xl shadow-lg border-l-4 border-indigo-500">
            <h2 className="text-3xl font-bold text-[#141414] mb-6 flex items-center gap-3">
              <Cloud size={32} className="text-indigo-500"/> {t('telefonia.virtual_pbx.title')}
            </h2>
            <p className="text-gray-700 mb-6 max-w-3xl">
              {t('telefonia.virtual_pbx.subtitle')}
            </p>

            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-sm text-gray-700 p-4 rounded-lg bg-indigo-50 border border-indigo-200">

                <div className="flex items-start gap-3">
                    <Cloud size={20} className="text-indigo-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong>{t('telefonia.virtual_pbx.features.cloud.title')}</strong>
                        <p className="text-gray-600">{t('telefonia.virtual_pbx.features.cloud.text')}</p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <Zap size={20} className="text-indigo-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong>{t('telefonia.virtual_pbx.features.voicebot.title')}</strong>
                        <p className="text-gray-600">{t('telefonia.virtual_pbx.features.voicebot.text')}</p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <TrendingUp size={20} className="text-indigo-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong>{t('telefonia.virtual_pbx.features.productivity.title')}</strong>
                        <p className="text-gray-600">{t('telefonia.virtual_pbx.features.productivity.text')}</p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <ShieldCheck size={20} className="text-indigo-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong>{t('telefonia.virtual_pbx.features.bidirectional.title')}</strong>
                        <p className="text-gray-600">{t('telefonia.virtual_pbx.features.bidirectional.text')}</p>
                    </div>
                </div>
            </div>
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-700 mb-2"></p>
              <div id="myZadarmaCallmeWidget16188" className="inline-block"></div>
            </div>
          </section>

          {/* SECCIÓN NUEVA: ¿Qué es la Telefonía IP? (Contraste) */}
          <section className="mt-8 p-8 bg-white rounded-xl shadow-lg border-l-4 border-blue-500">
            <h2 className="text-3xl font-bold text-[#141414] mb-6 flex items-center gap-3">
              <Signal size={32} className="text-blue-500"/> {t('telefonia.ip_vs_trad.title')}
            </h2>
            <p className="text-gray-700 mb-6 max-w-3xl">
              {t('telefonia.ip_vs_trad.subtitle')}
            </p>

            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-sm text-gray-700 p-4 rounded-lg bg-blue-50 border border-blue-200">

                <div className="flex items-start gap-3">
                    <TrendingUp size={20} className="text-blue-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong>{t('telefonia.ip_vs_trad.ip.title')}</strong>
                        <p className="text-gray-600">{t('telefonia.ip_vs_trad.ip.text')}</p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <PhoneOff size={20} className="text-red-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong>{t('telefonia.ip_vs_trad.trad.title')}</strong>
                        <p className="text-gray-600">{t('telefonia.ip_vs_trad.trad.text')}</p>
                    </div>
                </div>
            </div>
          </section>

          {/* SECCIÓN NUEVA: TIPOS DE LÍNEAS DISPONIBLES */}
          <section className="mt-8 p-8 bg-white rounded-xl shadow-lg border-l-4 border-gray-400">
            <h3 className="text-2xl font-bold text-[#141414] mb-4 flex items-center gap-2">
              <Globe size={24} className="text-gray-600"/> {t('telefonia.lines.title')}
            </h3>
            <p className="text-gray-700 mb-6">
              {t('telefonia.lines.subtitle')}
            </p>

            <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <Briefcase size={22} className="text-purple-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong>{t('telefonia.lines.fixed.title')}</strong>
                        <p className="text-sm text-gray-600">{t('telefonia.lines.fixed.text')}</p>
                    </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <Smartphone size={22} className="text-purple-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong>{t('telefonia.lines.mobile.title')}</strong>
                        <p className="text-sm text-gray-600">{t('telefonia.lines.mobile.text')}</p>
                    </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <DollarSign size={22} className="text-purple-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong>{t('telefonia.lines.toll_free.title')}</strong>
                        <p className="text-sm text-gray-600">{t('telefonia.lines.toll_free.text')}</p>
                    </div>
                </div>
            </div>
          </section>

          {/* CTA FINAL */}
          {/* Botón CTA Final: AHORA LLAMA handleCall */}
          <section className="max-w-4xl mx-auto mt-24">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-xl p-8 md:p-12 text-center shadow-lg">
              <h2 className="text-3xl font-bold mb-4">
                {t('telefonia.final_cta.title')}
              </h2>
              <p className="text-lg opacity-90 max-w-xl mx-auto mb-8">
                {t('telefonia.final_cta.subtitle')}
              </p>
              <button
                onClick={handleCall}
                className="inline-flex items-center justify-center gap-2 bg-[#141414] text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition font-semibold shadow-xl transform hover:scale-[1.02]"
              >
                <Send size={18} />
                {t('telefonia.final_cta.btn')}
              </button>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default TelefoniaPage;


